import { DOMParser as PMDOMParser, DOMSerializer, Node, Schema } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { prosemirrorJSONToYDoc, prosemirrorToYDoc } from 'y-prosemirror';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';

import { transformListItemLikeElementsIntoLists } from './utilsList';

const EDITOR_CONTAINER_CLASSNAME = 'editor-container';
export const getEditorWidth = () =>
  document.querySelector('.ProseMirror')?.getBoundingClientRect().width;

export const getEditorContainerClientRect = () =>
  document.querySelector(`.${EDITOR_CONTAINER_CLASSNAME}`)?.getBoundingClientRect();

export const getEditorContainerInnerWidth = () =>
  document.querySelector(`.${EDITOR_CONTAINER_CLASSNAME}`)?.clientWidth;

const fetchHtmlAsText = async (url: string) => {
  return await (await fetch(url)).text();
};

export const importEditorFromHTML = async (fileUrl: string, editorView: EditorView) => {
  let htmlString, pmDoc;
  try {
    htmlString = await fetchHtmlAsText(fileUrl);
    htmlString = htmlString.replaceAll(
      'src="',
      `src="${fileUrl.replace(fileUrl.split('/').pop()!, '')}`,
    );
  } catch (e) {
    throw new Error('[importEditorFromHTML] problem parsing the HTML file');
  }

  try {
    const htmlDoc = new DOMParser().parseFromString(htmlString, 'text/html');
    // The Resultant HTML will contain an container div in the body. We want to remove that div
    // and shift all of it's children under the body tag.
    const htmlDocElement = htmlDoc.body.children[0];
    // @ts-ignore
    htmlDoc.body.append(...htmlDocElement.childNodes);
    htmlDocElement.remove();
    transformListItemLikeElementsIntoLists(htmlDoc);
    pmDoc = PMDOMParser.fromSchema(editorView.state.schema).parse(htmlDoc.body);
  } catch (e) {
    throw new Error('[importEditorFromHTML] problem generating the prosemirror doc');
  }

  try {
    let newState = EditorState.create({
      schema: editorView.state.schema,
      doc: pmDoc,
      plugins: editorView.state.plugins,
    });
    editorView.updateState(newState);
    return true;
  } catch (e) {
    throw new Error('[importEditorFromHTML] problem inserting the prosemirror doc into the state');
  }
};

export const getHTMLFromDoc = (doc: Node, schema: Schema) => {
  const htmlValue = DOMSerializer.fromSchema(schema).serializeFragment(doc.content);

  const temporaryDocument = document.implementation.createHTMLDocument();
  const container = temporaryDocument.createElement('div');

  container.appendChild(htmlValue);

  // Convert font-semibold spans to proper strong tags
  const htmlString = container.innerHTML === '<p></p>' ? '' : container.innerHTML;
  return htmlString
    .replace(/<span class="font-semibold">/g, '<strong>')
    .replace(/<\/span>/g, '</strong>');
};

export const getPMNodeCount = (editorView: EditorView) => {
  return editorView?.state?.doc?.childCount;
};

export { transformListItemLikeElementsIntoLists } from './utilsList';

/**
 * Seed a Yjs document with a default video node if the fragment is empty.
 * @param schema - ProseMirror schema containing a 'video' node type
 * @param yProvider - Yjs provider instance
 * @param fragmentName - Name of the XmlFragment, usually 'prosemirror'
 */

export function seedYDocWithDefaultVideo(
  schema: Schema,
  yProvider: WebsocketProvider | undefined,
  fragmentName: string = 'prosemirror',
) {
  if (!yProvider) return;

  const type = yProvider.doc.getXmlFragment(fragmentName);

  // Check if the fragment already has any node
  const hasContent = type.toArray().some((el: any) => el.nodeName);

  if (hasContent) {
    // Document already has content, do not insert
    return;
  } else {
    console.log('seeding doc with video', type, type.toArray());
  }

  const videoNode = schema.nodes.video?.createAndFill?.();
  if (!videoNode) return;

  // Create a doc node containing only the video
  const pmDocNode = schema.node('doc', null, [videoNode]);

  let initYDoc: Y.Doc | undefined;
  try {
    if (typeof prosemirrorJSONToYDoc === 'function') {
      initYDoc = prosemirrorJSONToYDoc(schema, pmDocNode.toJSON());
    } else if (typeof prosemirrorToYDoc === 'function') {
      initYDoc = prosemirrorToYDoc(pmDocNode);
    }
  } catch (err) {
    console.error('Error converting PM doc to Y.Doc', err);
    return;
  }

  if (initYDoc) {
    const update = Y.encodeStateAsUpdate(initYDoc);
    Y.applyUpdate(yProvider.doc, update);
  }
}
